import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { getCompanyRolesAsync } from '@application/features/roles/rolesThunk';
import { compressImageTo1MB } from '@application/helpers/file-helper';
import { getAllLanguagesAsync } from '@application/features/languages/languagesThunk';
import { filterApplicationLanguages } from '@application/helpers/language-filter-helper';
import { useAppDispatch } from '@application/store/useStore';
import { Box, Button, Checkbox, Typography } from '@mui/material';
import ControlledHCheckboxAutocomplete from '@ui/components/common/Autocomplete/ControlledHCheckboxAutocomplete';
import SingleSelect from '@ui/components/common/Autocomplete/SingleSelect';
import { ControlledFilledTextField } from '@ui/components/common/TextField';
import { PhoneInputMasking } from '@ui/components/PhoneMasking/PhoneInputMasking';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef } from 'react';
import { useForm, useFormState, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import ImageUpload from './components/ImageUpload';
import useAddNewUserStyles from './useAddEditUserStyles';
const getCompanyRegionGroups = (companies) => {
    const regionGroups = {};
    companies.forEach((company) => {
        const regionLabel = company.regionLabel || '';
        if (!regionGroups[regionLabel]) {
            regionGroups[regionLabel] = [];
        }
        regionGroups[regionLabel].push(company.value);
    });
    return regionGroups;
};
const AddEditUser = forwardRef(({ onSubmit, onCancel, dialog = false, edit = false, user }, ref) => {
    const { t } = useTranslation();
    const initialized = useRef(false);
    const dispatch = useAppDispatch();
    const styles = useAddNewUserStyles();
    const prevUserId = useRef();
    const roles = useSelector((state) => state.role.companyRoles);
    const companies = useSelector((state) => state.company.companies);
    const companiesByRegion = useSelector((state) => state.company.companiesByRegion);
    const languages = useSelector((state) => state.languages.languages);
    const selectedUser = useSelector((state) => state.auth.currentUser);
    const companyRegionLookup = useMemo(() => {
        const lookup = {};
        companiesByRegion.forEach((region) => {
            region.companies.forEach((company) => {
                lookup[company.id] = region.regionLabel || '';
            });
        });
        return lookup;
    }, [companiesByRegion]);
    const companyOptions = useMemo(() => companies.map((c) => ({
        label: c.name,
        value: c.id,
        regionLabel: companyRegionLookup[c.id] || ''
    })), [companies, companyRegionLookup]);
    const defaultValues = edit && user
        ? {
            id: user.id,
            firstName: user.firstName,
            lastName: user.lastName,
            email: user.email,
            phoneNo: user.phoneNumber || '',
            // Transform for edit mode - form components expect objects
            companyIds: (user.companies || []).map((companyId) => {
                const company = companies.find((c) => c.id === companyId);
                return {
                    label: company?.name || 'Unknown Company',
                    value: companyId
                };
            }),
            primaryCompanyId: user.primaryCompanyId,
            roles: (user.roles || []).map((role) => {
                // Backend sends each role-company combination as separate objects
                // Check if this is a system role (companyId is all zeros) or company-specific role
                const isSystemRole = role.companyId === '00000000-0000-0000-0000-000000000000' || role.companyId === '';
                if (isSystemRole) {
                    // System role - use regular format
                    return {
                        label: role.name,
                        value: role.id,
                        companyId: role.companyId
                    };
                }
                else {
                    // Company-specific role - use roleId_companyId format
                    return {
                        label: `${role.name} (${role.companyName || 'Unknown Company'})`,
                        value: `${role.id}_${role.companyId}`,
                        companyId: role.companyId
                    };
                }
            }),
            primaryLanguageId: user.primaryLanguageId || undefined,
            profilePicture: user.profileImageUrl || '' // Add profile picture handling
        }
        : {
            // Add mode - start with empty arrays
            firstName: '',
            lastName: '',
            email: '',
            phoneNo: '',
            companyIds: [],
            primaryCompanyId: '',
            primaryLanguageId: undefined,
            roles: [],
            profilePicture: ''
        };
    const { control, handleSubmit, reset, setValue, getValues, formState: { errors } } = useForm({
        defaultValues,
        mode: 'onChange',
        reValidateMode: 'onChange',
        shouldFocusError: false
    });
    const { isSubmitting } = useFormState({ control });
    useEffect(() => {
        if (edit && user && user.id !== prevUserId.current) {
            initialized.current = false;
        }
    }, [edit, user]);
    useEffect(() => {
        if (edit && user && user.id !== prevUserId.current) {
            const transformedCompanyIds = (user.companies || []).map((companyId) => {
                const company = companies.find((c) => c.id === companyId);
                return {
                    label: company?.name || 'Unknown Company',
                    value: companyId
                };
            });
            const transformedRoles = (user.roles || []).map((role) => {
                // Backend sends each role-company combination as separate objects
                // Check if this is a system role (companyId is all zeros) or company-specific role
                const isSystemRole = role.companyId === '00000000-0000-0000-0000-000000000000' || role.companyId === '';
                if (isSystemRole) {
                    // System role - use regular format
                    return {
                        label: role.name,
                        value: role.id,
                        companyId: role.companyId
                    };
                }
                else {
                    // Company-specific role - use roleId_companyId format
                    return {
                        label: `${role.name} (${role.companyName || 'Unknown Company'})`,
                        value: `${role.id}_${role.companyId}`,
                        companyId: role.companyId
                    };
                }
            });
            reset({
                id: user.id,
                firstName: user.firstName,
                lastName: user.lastName,
                email: user.email,
                phoneNo: user.phoneNumber || '',
                companyIds: transformedCompanyIds,
                primaryCompanyId: user.primaryCompanyId,
                primaryLanguageId: user.primaryLanguageId || undefined,
                roles: transformedRoles,
                profilePicture: user.profileImageUrl || ''
            });
            // Ensure role options are refreshed for the user's current companies
            const companyIdsForFetch = (user.companies || []).map((companyId) => companyId);
            if (companyIdsForFetch.length > 0) {
                const payload = { companies: companyIdsForFetch };
                dispatch(getCompanyRolesAsync(payload));
            }
            prevUserId.current = user.id;
        }
        else if (!edit && (!user || user.id !== prevUserId.current)) {
            reset({
                firstName: '',
                lastName: '',
                email: '',
                phoneNo: '',
                companyIds: [],
                primaryCompanyId: '',
                primaryLanguageId: undefined,
                roles: [],
                profilePicture: ''
            });
            prevUserId.current = user?.id;
        }
    }, [edit, user, reset, companies, dispatch, languages]);
    const handleFormSubmit = async (data) => {
        const extractValue = (item) => {
            if (typeof item === 'object' && item !== null) {
                return item.value || item.id || item;
            }
            return item;
        };
        // Transform companyIds (handles both object[] and string[])
        const companyIds = Array.isArray(data.companyIds) ? data.companyIds.map(extractValue) : [];
        // Transform roles with proper type filtering
        const transformedRoles = Array.isArray(data.roles)
            ? data.roles
                .map((r) => {
                const roleValue = extractValue(r);
                // Check if this is a role-company combination (format: roleId_companyId)
                if (roleValue.includes('_')) {
                    const [roleId, companyId] = roleValue.split('_');
                    const role = roles.find((role) => role.id === roleId);
                    if (!role) {
                        return null;
                    }
                    return {
                        id: role.id,
                        name: role.name,
                        companyId: companyId,
                        isActive: true
                    };
                }
                else {
                    // Handle regular role selection (for roles with categoryId)
                    const role = roles.find((role) => role.id === roleValue);
                    if (!role) {
                        return null;
                    }
                    return {
                        id: role.id,
                        name: role.name,
                        companyId: role.companyId,
                        isActive: true
                    };
                }
            })
                .filter((role) => role !== null) // Type guard
            : [];
        // Transform primary company ID
        const primaryCompanyId = extractValue(data.primaryCompanyId);
        // Transform primary language ID
        const primaryLanguageId = data.primaryLanguageId
            ? extractValue(data.primaryLanguageId)
            : undefined;
        let profilePicturePayload = undefined;
        if (data.profilePicture) {
            if (typeof data.profilePicture === 'string') {
                // Existing image URL, send as-is or ignore if unchanged
                profilePicturePayload = data.profilePicture;
            }
            else if (data.profilePicture instanceof File) {
                // New image, send the File object
                profilePicturePayload = await compressImageTo1MB(data.profilePicture);
                // profilePicturePayload = data.profilePicture;
            }
        }
        const transformedData = {
            ...data,
            primaryCompanyId,
            primaryLanguageId,
            companyIds,
            roles: transformedRoles,
            profilePicture: profilePicturePayload
        };
        const payload = edit
            ? {
                ...transformedData,
                id: data.id || user.id
            }
            : transformedData;
        onSubmit?.(payload);
    };
    useImperativeHandle(ref, () => ({
        submitForm: async () => {
            handleSubmit(handleFormSubmit)();
        }
    }));
    const watchedCompanyIds = useWatch({
        control,
        name: 'companyIds'
    });
    const watchedPrimaryCompanyId = useWatch({
        control,
        name: 'primaryCompanyId'
    });
    const regionGroups = useMemo(() => getCompanyRegionGroups(companyOptions), [companyOptions]);
    const handleCompanyRegionToggle = useCallback((regionLabel) => {
        const regionCompanyIds = regionGroups[regionLabel] || [];
        if (!regionCompanyIds.length)
            return;
        const currentCompanies = watchedCompanyIds || [];
        const currentCompanyIds = currentCompanies.map((c) => typeof c === 'object' && c.value ? c.value : c);
        const allSelected = regionCompanyIds.every((id) => currentCompanyIds.includes(id));
        let updatedSelection;
        if (allSelected) {
            // Deselect all companies in this region
            updatedSelection = currentCompanies.filter((c) => {
                const id = typeof c === 'object' && c.value ? c.value : c;
                return !regionCompanyIds.includes(id);
            });
        }
        else {
            // Select all companies in this region (merge + de-duplicate)
            const newCompanies = companyOptions.filter((opt) => regionCompanyIds.includes(opt.value) && !currentCompanyIds.includes(opt.value));
            updatedSelection = [...currentCompanies, ...newCompanies];
        }
        setValue('companyIds', updatedSelection);
    }, [companyOptions, regionGroups, setValue, watchedCompanyIds]);
    useEffect(() => {
        if (isSubmitting)
            return;
        if (isHappinessManagerSelected)
            return;
        if (watchedCompanyIds && watchedCompanyIds.length > 0) {
            const companyIds = watchedCompanyIds.map((item) => typeof item === 'object' && item !== null ? item.value : item);
            const payload = { companies: companyIds };
            dispatch(getCompanyRolesAsync(payload));
        }
        else {
            setValue('roles', []);
            setValue('primaryCompanyId', '');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [watchedCompanyIds]);
    useEffect(() => {
        if (watchedPrimaryCompanyId && watchedCompanyIds) {
            const selectedCompanyValues = watchedCompanyIds.map((item) => typeof item === 'object' && item !== null ? item.value : item);
            let primaryCompanyValue;
            if (typeof watchedPrimaryCompanyId === 'object' &&
                watchedPrimaryCompanyId !== null &&
                'value' in watchedPrimaryCompanyId) {
                primaryCompanyValue = watchedPrimaryCompanyId.value;
            }
            else if (typeof watchedPrimaryCompanyId === 'string') {
                primaryCompanyValue = watchedPrimaryCompanyId;
            }
            const isPrimaryCompanyInSelectedCompanies = primaryCompanyValue
                ? selectedCompanyValues.includes(primaryCompanyValue)
                : false;
            if (!isPrimaryCompanyInSelectedCompanies) {
                setValue('primaryCompanyId', '');
            }
        }
    }, [watchedCompanyIds, watchedPrimaryCompanyId, setValue]);
    const watchedRoles = useWatch({
        control,
        name: 'roles'
    });
    // Check if Happiness Manager role is selected
    const isHappinessManagerSelected = useMemo(() => {
        if (!watchedRoles || !roles)
            return false;
        const selectedRoleIds = watchedRoles.map((r) => ('id' in r ? r.id : r.value));
        const happinessManagerRole = roles.find((role) => role.name === 'HappinessManager' ||
            (role.displayName === 'Happiness Manager' && role.isSystem === true));
        return happinessManagerRole ? selectedRoleIds.includes(happinessManagerRole.id) : false;
    }, [watchedRoles, roles]);
    // Auto-select all companies when Happiness Manager is selected
    // Do NOT clear companies when role changes
    useEffect(() => {
        if (isHappinessManagerSelected && companies.length > 0) {
            // Select all companies when Happiness Manager is selected
            setValue('companyIds', companyOptions);
            // Set primary company to first available company if not already set
            const currentPrimaryCompany = getValues('primaryCompanyId');
            if (!currentPrimaryCompany && companyOptions.length > 0) {
                setValue('primaryCompanyId', {
                    label: companyOptions[0].label,
                    value: companyOptions[0].value
                });
            }
        }
        // Removed: clearing companies when switching away from Happiness Manager
        // Companies should remain selected regardless of role changes
    }, [isHappinessManagerSelected, companies, companyOptions, setValue, getValues]);
    useEffect(() => {
        if (!initialized.current) {
            initialized.current = true;
            return;
        }
        if (!watchedCompanyIds)
            return;
        const selectedCompanyValues = watchedCompanyIds
            .map((item) => (typeof item === 'object' && item !== null ? item.value : item))
            .filter((id) => !!id);
        // Handle primary company cleanup:
        let primaryCompanyValue;
        if (typeof watchedPrimaryCompanyId === 'object' &&
            watchedPrimaryCompanyId !== null &&
            'value' in watchedPrimaryCompanyId) {
            primaryCompanyValue = watchedPrimaryCompanyId.value;
        }
        else if (typeof watchedPrimaryCompanyId === 'string') {
            primaryCompanyValue = watchedPrimaryCompanyId;
        }
        const isPrimaryCompanyValid = primaryCompanyValue
            ? selectedCompanyValues.includes(primaryCompanyValue)
            : false;
        if (primaryCompanyValue && !isPrimaryCompanyValid) {
            setValue('primaryCompanyId', '');
        }
        // Handle role cleanup:
        const cleanupSelectedCompanyValues = watchedCompanyIds?.map((item) => typeof item === 'object' && item !== null ? item.value : item) || [];
        const validRoles = (watchedRoles || [])
            .map((roleObj) => {
            const roleValue = 'id' in roleObj ? roleObj.id : roleObj.value;
            // Get stored categoryId and name from the role object if available
            const storedCategoryId = 'categoryId' in roleObj ? roleObj.categoryId : undefined;
            const storedName = 'name' in roleObj ? roleObj.name : undefined;
            // Check if this is a role-company combination
            if (roleValue.includes('_')) {
                const [roleId, companyId] = roleValue.split('_');
                let role;
                // Try to find by ID first to get name and categoryId if not stored
                const roleById = roles.find((r) => r.id === roleId);
                const effectiveCategoryId = storedCategoryId || roleById?.categoryId;
                const effectiveName = storedName || roleById?.name;
                // Check if this is a system role (has categoryId)
                const isSystemRole = effectiveCategoryId !== null && effectiveCategoryId !== undefined;
                // For system roles only, prioritize matching by categoryId and name
                // This handles the case where the backend returns a different ID for the same system role per company
                if (isSystemRole && effectiveName) {
                    // Try to find system role by categoryId and name for the specific company or any selected company
                    role = roles.find((r) => r.name === effectiveName &&
                        r.categoryId === effectiveCategoryId &&
                        (r.companyId === companyId ||
                            r.companies?.some((c) => c.id === companyId) ||
                            // If the original company is no longer selected, find the role for any selected company
                            (cleanupSelectedCompanyValues.length > 0 &&
                                (cleanupSelectedCompanyValues.includes(r.companyId || '') ||
                                    r.companies?.some((c) => cleanupSelectedCompanyValues.includes(c.id)) ||
                                    r.companyId === '00000000-0000-0000-0000-000000000000' ||
                                    r.companyId === ''))));
                }
                // If not found by categoryId/name (or not a system role), use the role found by ID
                if (!role) {
                    role = roleById;
                }
                if (role) {
                    // Check if this role-company combination is still valid for selected companies
                    const isRoleSystemRole = role.categoryId !== null && role.categoryId !== undefined;
                    let isRoleCompanyValid = isRoleSystemRole ||
                        (role.companies &&
                            role.companies.some((c) => c.id === companyId && cleanupSelectedCompanyValues.includes(c.id))) ||
                        (role.companyId === companyId &&
                            cleanupSelectedCompanyValues.includes(companyId)) ||
                        companyId === '00000000-0000-0000-0000-000000000000' ||
                        companyId === '';
                    // If it's a system role but the original company is no longer selected,
                    // use the role's current companyId (which should be for a selected company)
                    if (isRoleSystemRole &&
                        !cleanupSelectedCompanyValues.includes(companyId) &&
                        cleanupSelectedCompanyValues.length > 0) {
                        // The role found should already be for a selected company due to our matching logic above
                        // Use the role's companyId instead of the original companyId
                        const validCompanyId = role.companyId && cleanupSelectedCompanyValues.includes(role.companyId)
                            ? role.companyId
                            : role.companies?.find((c) => cleanupSelectedCompanyValues.includes(c.id))
                                ?.id || cleanupSelectedCompanyValues[0];
                        if (validCompanyId) {
                            isRoleCompanyValid = true;
                            // Update companyId to the valid one
                            const updatedRoleValue = `${role.id}_${validCompanyId}`;
                            const company = companies.find((c) => c.id === validCompanyId);
                            return {
                                label: `${role.displayName} (${company?.name || 'Unknown Company'})`,
                                value: updatedRoleValue,
                                name: role.name,
                                companyId: validCompanyId,
                                categoryId: role.categoryId,
                                isDisabled: false
                            };
                        }
                    }
                    if (isRoleCompanyValid) {
                        // Use the found role's ID (which might be different from the original)
                        const updatedRoleValue = `${role.id}_${companyId}`;
                        return {
                            label: `${role.displayName} (${role.companies?.find((c) => c.id === companyId)?.name || companies.find((c) => c.id === companyId)?.name || 'Unknown Company'})`,
                            value: updatedRoleValue,
                            name: role.name,
                            companyId: companyId,
                            categoryId: role.categoryId,
                            isDisabled: false
                        };
                    }
                }
                return null;
            }
            else {
                // Handle regular role selection
                let role;
                // Try to find by ID first to get name and categoryId if not stored
                const roleById = roles.find((r) => r.id === roleValue);
                const effectiveCategoryId = storedCategoryId || roleById?.categoryId;
                const effectiveName = storedName || roleById?.name;
                // Check if this is a system role (has categoryId)
                const isSystemRole = effectiveCategoryId !== null && effectiveCategoryId !== undefined;
                // For system roles only, prioritize matching by categoryId and name
                // This handles the case where the backend returns a different ID for the same system role per company
                if (isSystemRole && effectiveName) {
                    // First, try to find system role by categoryId and name that belongs to selected companies
                    role = roles.find((r) => r.name === effectiveName &&
                        r.categoryId === effectiveCategoryId &&
                        // Ensure the role belongs to at least one selected company
                        (cleanupSelectedCompanyValues.length === 0 ||
                            cleanupSelectedCompanyValues.some((companyId) => r.companyId === companyId ||
                                r.companies?.some((c) => c.id === companyId) ||
                                r.companyId === '00000000-0000-0000-0000-000000000000' ||
                                r.companyId === '')));
                }
                // If not found by categoryId/name (or not a system role), use the role found by ID
                if (!role) {
                    role = roleById;
                }
                if (role) {
                    // Check if this role is still valid for selected companies
                    const isRoleSystemRole = role.categoryId !== null && role.categoryId !== undefined;
                    let isRoleValid = isRoleSystemRole ||
                        (role.companies &&
                            role.companies.some((c) => cleanupSelectedCompanyValues.includes(c.id))) ||
                        (role.companyId && cleanupSelectedCompanyValues.includes(role.companyId)) ||
                        role.companyId === '00000000-0000-0000-0000-000000000000' ||
                        role.companyId === '';
                    // If it's a system role but doesn't belong to selected companies,
                    // try to find the same system role for a selected company
                    if (isRoleSystemRole && !isRoleValid && cleanupSelectedCompanyValues.length > 0) {
                        const alternativeRole = roles.find((r) => r.name === role.name &&
                            r.categoryId === role.categoryId &&
                            (cleanupSelectedCompanyValues.includes(r.companyId || '') ||
                                r.companies?.some((c) => cleanupSelectedCompanyValues.includes(c.id)) ||
                                r.companyId === '00000000-0000-0000-0000-000000000000' ||
                                r.companyId === ''));
                        if (alternativeRole) {
                            role = alternativeRole;
                            isRoleValid = true;
                        }
                    }
                    if (isRoleValid) {
                        return {
                            label: role.displayName,
                            value: role.id,
                            name: role.name,
                            companyId: role.companyId,
                            categoryId: role.categoryId,
                            isDisabled: false
                        };
                    }
                }
                return null;
            }
        })
            .filter((role) => role !== null);
        const currentRoleValues = (watchedRoles || []).map((r) => ('value' in r ? r.value : r));
        const newRoleValues = validRoles.map((r) => r.value);
        const isRolesChanged = JSON.stringify(currentRoleValues) !== JSON.stringify(newRoleValues);
        //const isRolesChanged = JSON.stringify(validRoles) !== JSON.stringify(watchedRoles);
        if (isRolesChanged) {
            setValue('roles', validRoles);
        }
    }, [
        watchedCompanyIds,
        watchedCompanyIds?.length,
        watchedPrimaryCompanyId,
        watchedRoles,
        companies,
        roles,
        setValue
    ]);
    // const primaryCompanyOptions: CompanyOption[] = useMemo(() => {
    //   if (edit && user && user.companies) {
    //     // In edit mode, show all user's companies
    //     return user.companies.map((companyId) => {
    //       const company = companies.find((c) => c.id === companyId);
    //       return {
    //         label: company?.name || 'Unknown Company',
    //         value: companyId
    //       };
    //     });
    //   }
    //   // In add mode, show only selected companies
    //   return watchedCompanyIds && watchedCompanyIds.length > 0
    //     ? companyOptions.filter((option) => {
    //         const selectedValues = watchedCompanyIds.map((item) =>
    //           typeof item === 'object' && item !== null ? item.value : item
    //         );
    //         return selectedValues.includes(option.value);
    //       })
    //     : [];
    // }, [watchedCompanyIds, companyOptions, edit, user, companies]);
    const primaryCompanyOptions = useMemo(() => {
        // Always show only currently selected companies
        if (watchedCompanyIds && watchedCompanyIds.length > 0) {
            const selectedValues = watchedCompanyIds.map((item) => typeof item === 'object' && item !== null ? item.value : item);
            return companyOptions.filter((option) => selectedValues.includes(option.value));
        }
        return [];
    }, [watchedCompanyIds, companyOptions]);
    // ✅ Safe type handling for roles
    const selectedRoleValues = (useWatch({ control, name: 'roles' }) || []);
    const selectedRoleIds = selectedRoleValues.map((r) => ('id' in r ? r.id : r.value));
    const computedRoleOptions = useMemo(() => {
        // If no roles available, return empty array
        if (!roles || roles.length === 0)
            return [];
        // Get currently selected company IDs
        const selectedCompanyValues = watchedCompanyIds?.map((item) => typeof item === 'object' && item !== null ? item.value : item) || [];
        // If no companies are selected, return empty array
        if (selectedCompanyValues.length === 0)
            return [];
        // Filter roles to only include those belonging to selected companies
        const filteredRoles = roles.filter((role) => {
            // For roles with categoryId (system roles), always show them
            if (role.categoryId !== null && role.categoryId !== undefined) {
                return true;
            }
            // For roles without categoryId, check if they belong to selected companies
            if (role.companies && role.companies.length > 0) {
                // Check if any of the role's companies are in the selected companies
                return role.companies.some((company) => selectedCompanyValues.includes(company.id));
            }
            else if (role.companyId) {
                // Check if this is a system role (all zeros companyId) - always show these
                const isSystemRole = role.companyId === '00000000-0000-0000-0000-000000000000' || role.companyId === '';
                if (isSystemRole) {
                    return true;
                }
                // Fallback to single companyId check
                return selectedCompanyValues.includes(role.companyId);
            }
            return false;
        });
        // Role selection rules:
        // 1. For roles with categoryId (not null): only one can be selected at a time
        // 2. For roles without categoryId (null): only one per companyId can be selected
        // 3. Display format: roles without categoryId show as "roleName (companyName)"
        // 4. For roles with categoryId null: create separate options for each company
        // Check if any role with categoryId is selected
        const hasSelectedCategoryRole = selectedRoleIds.some((roleId) => {
            const role = roles.find((r) => r.id === roleId.split('_')[0]);
            return role && role.categoryId !== null && role.categoryId !== undefined;
        });
        // Get selected company IDs for roles without category
        // This includes both regular role selections and role-company combinations
        const selectedCompanyIds = [];
        selectedRoleIds.forEach((roleId) => {
            if (roleId.includes('_')) {
                // This is a role-company combination
                const parts = roleId.split('_');
                const companyId = parts[1];
                selectedCompanyIds.push(companyId);
            }
            else {
                // This is a regular role selection
                const role = roles.find((r) => r.id === roleId);
                if (role && (role.categoryId === null || role.categoryId === undefined)) {
                    // Only add company ID if it's not a system role (all zeros)
                    if (role.companyId &&
                        role.companyId !== '00000000-0000-0000-0000-000000000000' &&
                        role.companyId !== '') {
                        selectedCompanyIds.push(role.companyId);
                    }
                }
            }
        });
        const options = [];
        filteredRoles.forEach((role) => {
            const hasCategory = role.categoryId !== null && role.categoryId !== undefined;
            if (hasCategory) {
                // For roles with categoryId: create single option
                const isSelected = selectedRoleIds.includes(role.id);
                const isDisabled = hasSelectedCategoryRole && !isSelected;
                options.push({
                    label: role.displayName,
                    value: role.id,
                    name: role.name,
                    companyId: role.companyId,
                    categoryId: role.categoryId,
                    isDisabled
                });
            }
            else {
                // Check if this is a system role (all zeros companyId)
                const isSystemRole = role.companyId === '00000000-0000-0000-0000-000000000000' || role.companyId === '';
                if (isSystemRole) {
                    // System role - create single option without company name
                    const isDisabled = false; // System roles are never disabled by company selection
                    options.push({
                        label: role.displayName,
                        value: role.id,
                        name: role.name,
                        companyId: role.companyId,
                        categoryId: role.categoryId,
                        isDisabled
                    });
                }
                else {
                    // For roles without categoryId: create separate option for each company
                    if (role.companies && role.companies.length > 0) {
                        // Filter companies to only include selected ones
                        const relevantCompanies = role.companies.filter((company) => selectedCompanyValues.includes(company.id));
                        relevantCompanies.forEach((company) => {
                            const uniqueValue = `${role.id}_${company.id}`;
                            const isSelected = selectedRoleIds.includes(uniqueValue);
                            const isDisabled = selectedCompanyIds.includes(company.id) && !isSelected;
                            options.push({
                                label: `${role.displayName} (${company.name})`,
                                value: uniqueValue,
                                name: role.name,
                                companyId: company.id,
                                categoryId: role.categoryId,
                                isDisabled
                            });
                        });
                    }
                    else if (role.companyId && selectedCompanyValues.includes(role.companyId)) {
                        // Fallback to single companyId if companies array is not available
                        const company = companies.find((c) => c.id === role.companyId);
                        const companyName = company?.name || 'Unknown Company';
                        const isSelectedSimple = selectedRoleIds.includes(role.id);
                        const isDisabled = selectedCompanyIds.includes(role.companyId) && !isSelectedSimple;
                        options.push({
                            label: `${role.displayName} (${companyName})`,
                            value: role.id,
                            name: role.name,
                            companyId: role.companyId,
                            categoryId: role.categoryId,
                            isDisabled
                        });
                    }
                }
            }
        });
        return options;
    }, [roles, selectedRoleIds, companies, watchedCompanyIds]);
    // Load languages on component mount
    useEffect(() => {
        dispatch(getAllLanguagesAsync());
    }, [dispatch]);
    // Transform languages for dropdown with flag icons
    const languageOptions = useMemo(() => {
        const filteredLanguages = filterApplicationLanguages(languages);
        return (filteredLanguages || []).map((lang) => ({
            label: lang.name,
            value: lang.id,
            icon: lang.flagUrl ? (_jsx(Box, { component: 'img', src: lang.flagUrl, alt: lang.name, sx: {
                    width: 20,
                    height: 15,
                    objectFit: 'cover',
                    borderRadius: 0.5
                } })) : undefined
        }));
    }, [languages]);
    // Update primaryLanguageId when languages are loaded and user has a primaryLanguageId
    // SingleSelect expects just the value string, not the full object
    useEffect(() => {
        if (edit && user && user.primaryLanguageId && languages && languages.length > 0) {
            const language = languages.find((l) => l.id === user.primaryLanguageId);
            if (language) {
                // Only update if the current value is different or not set
                const currentValue = getValues('primaryLanguageId');
                const currentValueId = typeof currentValue === 'object' && currentValue?.value
                    ? currentValue.value
                    : typeof currentValue === 'string'
                        ? currentValue
                        : undefined;
                if (currentValueId !== user.primaryLanguageId) {
                    // SingleSelect expects just the value string, it will find the option automatically
                    setValue('primaryLanguageId', user.primaryLanguageId);
                }
            }
        }
    }, [edit, user, languages, setValue, getValues]);
    const isSelfEdit = edit && user && selectedUser && user.id === selectedUser.id;
    const isCompanySelectionDisabled = isSelfEdit || isHappinessManagerSelected;
    return (_jsxs(Box, { sx: { ...styles.main }, children: [_jsx(ImageUpload, { control: control, edit: edit, name: 'profilePicture', rules: {
                    validate: (value) => {
                        if (!value)
                            return true; // Optional field
                        if (typeof value === 'string')
                            return true; // Existing image URL (edit mode)
                        if (value instanceof File) {
                            const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];
                            if (!allowedTypes.includes(value.type.toLowerCase())) {
                                return t('users.fileTypeError'); // Always return string
                            }
                            // Example: file size validation (max 2MB)
                            if (value.size > 2 * 1024 * 1024) {
                                return t('users.fileSizeError');
                            }
                        }
                        return true; // Always return true if valid
                    }
                } }), _jsx(ControlledFilledTextField, { control: control, bg: 'grey8', inputRef: ref, name: 'firstName', label: t('users.firstName'), rules: {
                    required: t('validations.required', { field: t('users.firstName') })
                }, errorMessage: errors.firstName?.message }), _jsx(ControlledFilledTextField, { control: control, bg: 'grey8', name: 'lastName', label: t('users.lastName'), rules: {
                    required: t('validations.required', { field: t('users.lastName') })
                }, errorMessage: errors.lastName?.message }), _jsx(ControlledHCheckboxAutocomplete, { bg: 'grey8', control: control, label: t('users.company'), name: 'companyIds', rules: {
                    required: t('validations.required', { field: t('users.company') })
                }, showSelectAll: true, isSortable: false, selectAllLabel: t('actions.selectAll'), error: errors.companyIds?.message, options: companyOptions, disabled: isCompanySelectionDisabled, 
                // Group companies by region
                groupBy: (option) => option.regionLabel || '', renderGroup: (params) => {
                    const regionLabel = params.group;
                    const regionCompanyIds = regionGroups[regionLabel] || [];
                    const currentCompanyIds = (watchedCompanyIds || []).map((c) => typeof c === 'object' && c.value ? c.value : c);
                    const allSelected = regionCompanyIds.length > 0 &&
                        regionCompanyIds.every((id) => currentCompanyIds.includes(id));
                    const someSelected = !allSelected && regionCompanyIds.some((id) => currentCompanyIds.includes(id));
                    const isUngrouped = !regionLabel;
                    return (_jsxs("li", { children: [_jsxs(Box, { display: 'flex', justifyContent: 'space-between', alignItems: 'center', sx: { pr: 1 }, onClick: (e) => e.stopPropagation(), children: [_jsx(Typography, { variant: 'subtitle2', sx: { pl: 2, pt: 1, pb: 0.5, color: 'text.secondary' }, children: regionLabel }), !isUngrouped && (_jsx(Checkbox, { size: 'small', checked: allSelected, indeterminate: someSelected, disabled: isCompanySelectionDisabled, onClick: (e) => {
                                            e.stopPropagation();
                                            if (!isCompanySelectionDisabled) {
                                                handleCompanyRegionToggle(regionLabel);
                                            }
                                        }, sx: {
                                            '&.Mui-disabled': {
                                                opacity: 0.5
                                            }
                                        } }))] }), _jsx("ul", { style: { padding: 0, margin: 0 }, children: params.children })] }, params.key));
                } }), _jsx(SingleSelect, { bg: 'grey8', control: control, label: t('users.primaryCompany'), name: 'primaryCompanyId', rules: {
                    required: t('validations.required', {
                        field: t('users.primaryCompany')
                    })
                }, disabled: isSelfEdit ?? false, options: primaryCompanyOptions, error: errors.primaryCompanyId?.message }), _jsx(SingleSelect, { bg: 'grey8', control: control, label: t('labels.primaryLanguage'), name: 'primaryLanguageId', options: languageOptions, error: errors.primaryLanguageId?.message }), _jsx(ControlledHCheckboxAutocomplete, { bg: 'grey8', control: control, label: t('users.role'), name: 'roles', treatDisabledAsMandatory: false, rules: {
                    validate: {
                        required: (value) => {
                            // Handle different possible value types
                            if (!value)
                                return t('validations.required', { field: t('users.role') });
                            // If it's an array, check length
                            if (Array.isArray(value)) {
                                return (value.length > 0 ||
                                    t('validations.required', { field: t('users.role') }));
                            }
                            // If it's not an array but has a truthy value, it's valid
                            return true;
                        }
                    }
                }, options: computedRoleOptions, error: errors.roles?.message, disabled: isSelfEdit ?? false }), _jsx(ControlledFilledTextField, { control: control, bg: 'grey8', rules: {
                    required: t('validations.required', { field: t('users.email') }),
                    pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: t('validations.invalidEmail', { field: t('users.email') })
                    }
                }, errorMessage: errors.email?.message, name: 'email', label: t('users.email') }), _jsx(PhoneInputMasking, { control: control, bg: 'grey8', label: t('users.phone'), name: 'phoneNo', error: errors.phoneNo?.message, rules: {
                    required: t('validations.required', { field: t('users.phone') }),
                    validate: (value) => {
                        // Accepts only numbers, must be at least 8 digits
                        const phoneRegex = /^\d{8,}$/;
                        if (!phoneRegex.test(value || '')) {
                            return t('validations.invalidPhone', { field: t('users.phone') });
                        }
                        return true;
                    }
                } }), !dialog && (_jsxs(Box, { sx: styles.actions, children: [_jsx(Button, { fullWidth: true, onClick: onCancel, variant: 'outlined', color: 'inherit', size: 'large', children: t('actions.cancel') }), _jsx(Button, { fullWidth: true, variant: 'contained', onClick: handleSubmit(handleFormSubmit), color: 'inherit', size: 'large', children: edit ? t('actions.editNow') : t('actions.addNow') })] }))] }));
});
AddEditUser.displayName = 'AddEditUser';
export default AddEditUser;
